/**
 * TypeScript view of the `hidden_article` format (ADR 0042).
 * Source of truth: `shared/schema/hidden_article.{definition,submission,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;
export type Language = "fr" | "en";

export const LANGUAGES: Language[] = ["fr", "en"];
export const DEFAULT_MIN_SIMILARITY = 50;
/** A hit at this figure is the word itself; anything below is a close word shown in its place. */
export const EXACT = 100;

/** Plain text (punctuation, spaces, given-away words) or a word to find, known by its length. */
export type Token = { kind: "text"; text: string } | { kind: "hidden"; length: number };

export interface Paragraph {
  tokens: Token[];
  /** A section heading rather than prose. */
  heading?: boolean;
}

export interface HiddenArticleDefinition {
  puzzleType: "hidden_article";
  typeVersion: number;
  language: Language;
  /** Word-vector file under shared/embeddings/, without extension. Defaults to the language. */
  vocabulary?: string;
  /** Cosine × 100 under which a guess is not shown next to a hidden word. */
  minSimilarity?: number;
  /** The page title, tokenised like a paragraph: its hidden words are the goal. */
  title: Token[];
  paragraphs: Paragraph[];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/**
 * `[hidden index, guess index, similarity]`; similarity `EXACT` means revealed. A revealed
 * inflected form (« chats » for the guess « chat ») carries the page's spelling fourth.
 */
export type Hit = [number, number, number] | [number, number, number, string];

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface HiddenArticleProgress {
  version: 1;
  /** The word being typed. */
  draft: string;
  /** Every guess so far, as the server normalised them, in submission order. */
  guesses: string[];
  /** What the last verdict revealed or brought close, for every hidden word concerned. */
  hits: Hit[];
  /** Title words revealed so far, from the last verdict. */
  titleFound?: number;
  /** The title is fully revealed: the page is found. */
  found?: boolean;
}

export interface HiddenArticleSubmission {
  guesses: string[];
}

export function minSimilarityOf(definition: HiddenArticleDefinition): number {
  return definition.minSimilarity ?? DEFAULT_MIN_SIMILARITY;
}
